<template>
  <form>
    <!-- first name -->
    <FormInputField
      v-model="firstName"
      :errors="v$.firstName.$errors"
      placeholder="First Name"
      autocomplete="given-name"
      @blur="v$.firstName.$validate()"
    />
    <!-- last name -->
    <FormInputField
      v-model="lastName"
      :errors="v$.lastName.$errors"
      placeholder="Last Name"
      autocomplete="family-name"
      @blur="v$.lastName.$validate()"
    />
    <!-- email -->
    <FormInputField
      v-model="email"
      :errors="v$.email.$errors"
      placeholder="Email"
      autocomplete="email"
      type="email"
      @blur="v$.email.$validate()"
    />
    <!-- phone -->
    <FormInputField
      v-model="phoneNumber"
      :errors="v$.phoneNumber.$errors"
      autocomplete="tel"
      placeholder="Phone number"
      type="tel"
      @blur="v$.phoneNumber.$validate()"
    />
    <!-- pronoun -->
    <FormDropdown
      :model-value="pronoun"
      class="mb-4"
      label="Pronoun"
      :options="pronouns"
      :errors="v$.pronoun.$errors"
      @update:model-value="setPronoun"
    />

    <slot name="extras" />
  </form>
</template>

<script lang="ts" setup>
import FormInputField from '@/components/FormInputField.vue'
import FormDropdown from '@/components/FormDropdown.vue'
import { CustomerDetails } from '@/graphql/types'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { PropType, ref, watch } from 'vue'
import { phoneNumberValidator, pronouns } from './profileHelpers'

export type RegistrationDetails = Omit<CustomerDetails, 'marketingOptIn' | 'birthday' | 'pronoun'> & {
  pronoun?: string
}

const validationRules = {
  email: { required },
  firstName: { required },
  lastName: { required },
  pronoun: {},
  phoneNumber: { required, phoneNumberValidator }
}

const props = defineProps({
  modelValue: {
    type: Object as PropType<RegistrationDetails>,
    required: false,
    default: () => ({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: ''
    })
  }
})

const emit = defineEmits(['update:modelValue'])

const firstName = ref(props.modelValue.firstName)
const lastName = ref(props.modelValue.lastName)
const email = ref(props.modelValue.email)
const phoneNumber = ref(props.modelValue.phoneNumber)
const pronoun = ref(props.modelValue.pronoun)

const v$ = useVuelidate(validationRules, {
  firstName,
  lastName,
  email,
  phoneNumber,
  pronoun
})

const setPronoun = ({ value }: { value: string }) => {
  pronoun.value = value
  v$.value.pronoun.$validate()
}

watch(
  () => ({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    phoneNumber: phoneNumber.value,
    pronoun: pronoun.value
  }),
  profile => {
    const isValid = v$?.value.$invalid === false
    if (isValid) emit('update:modelValue', profile)
    else emit('update:modelValue', undefined)
  }
)
</script>
